import type { GameFlow, GameFlowSnapshot } from "./game-flow";
import type { PlayerRuntime } from "./player-runtime";
import { gameplayConfig, type BonusId } from "../config/gameplay-config";
import {
  BonusPopulation,
  type BonusEntity,
} from "../simulation/bonus-population";
import type { Vector2 } from "../simulation/player-movement";
import { SeededRandom } from "../simulation/seeded-random";
import type { SpawnExclusionZone } from "../simulation/spawn-exclusions";

export interface BonusEvent {
  readonly bonusId: string;
  readonly occurredAtMs: number;
  readonly revision: number;
  readonly type: BonusId;
}

export interface BonusRuntimeSnapshot {
  readonly bonuses: readonly BonusEntity[];
  readonly collected: Readonly<Record<BonusId, number>>;
  readonly lastEvent: BonusEvent | null;
  readonly turboActiveRemainingMs: number;
  readonly turboBankedMs: number;
}

export const turboChargeDurationMs = gameplayConfig.bonuses[0].durationMs;

type PlayerPort = Pick<
  PlayerRuntime,
  "getSnapshot" | "setSpeedMultiplier" | "subscribeFixedStep"
>;

const segmentDistanceSquared = (
  point: Vector2,
  start: Vector2,
  end: Vector2,
): number => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) {
    const x = point.x - start.x;
    const y = point.y - start.y;
    return x * x + y * y;
  }
  const projection = Math.max(
    0,
    Math.min(
      1,
      ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared,
    ),
  );
  const x = point.x - (start.x + dx * projection);
  const y = point.y - (start.y + dy * projection);
  return x * x + y * y;
};

export class BonusRuntime {
  private collected: Record<BonusId, number> = { turbo: 0 };
  private lastEvent: BonusEvent | null = null;
  private readonly listeners = new Set<() => void>();
  private population: BonusPopulation | null = null;
  private previousPlayerPosition: Vector2 | null = null;
  private revision = 0;
  private scene: GameFlowSnapshot["scene"] = "home";
  private seed: string;
  private turboActiveRemainingMs = 0;
  private turboBankedMs = 0;

  public constructor(
    flow: GameFlow,
    private readonly playerRuntime: PlayerPort,
    seed: string,
    private readonly spawnExclusionSource: () => readonly SpawnExclusionZone[] = () => [],
  ) {
    this.seed = seed;
    playerRuntime.subscribeFixedStep((stepMilliseconds, simulationTimeMs) => {
      this.step(stepMilliseconds, simulationTimeMs);
    });
    flow.subscribe((snapshot) => {
      this.applyFlow(snapshot);
    });
  }

  public activateTurbo(): boolean {
    if (
      this.scene !== "gameplay" ||
      this.turboBankedMs <= 0 ||
      this.turboActiveRemainingMs > 0
    ) {
      return false;
    }
    this.turboActiveRemainingMs = turboChargeDurationMs;
    this.turboBankedMs -= turboChargeDurationMs;
    this.playerRuntime.setSpeedMultiplier(2);
    this.notify();
    return true;
  }

  public getSnapshot(): BonusRuntimeSnapshot {
    return {
      bonuses: this.population?.getSnapshot() ?? [],
      collected: { ...this.collected },
      lastEvent: this.lastEvent === null ? null : { ...this.lastEvent },
      turboActiveRemainingMs: this.turboActiveRemainingMs,
      turboBankedMs: this.turboBankedMs,
    };
  }

  public setSeed(seed: string): void {
    this.seed = seed;
  }

  public spawnNearPlayer(type: BonusId): string | null {
    const player = this.playerRuntime.getSnapshot().player;
    if (player === null || this.population === null) {
      return null;
    }
    return this.population.spawnNear(type, player.position).id;
  }

  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    listener();
    return () => {
      this.listeners.delete(listener);
    };
  }

  private applyFlow(snapshot: GameFlowSnapshot): void {
    this.scene = snapshot.scene;
    if (snapshot.scene !== "gameplay") {
      this.reset();
      return;
    }
    this.reset();
    this.population = new BonusPopulation({
      arena: gameplayConfig.arena,
      bonuses: gameplayConfig.bonuses,
      config: gameplayConfig.bonusPopulation,
      initialPlayerPosition: gameplayConfig.movement.initialPosition,
      random: new SeededRandom(this.seed),
    });
    this.previousPlayerPosition =
      this.playerRuntime.getSnapshot().player?.position ?? null;
    this.notify();
  }

  private collect(bonus: BonusEntity, simulationTimeMs: number): void {
    const removed = this.population?.remove(
      bonus.id,
      this.spawnExclusionSource(),
    );
    if (removed === null || removed === undefined) {
      return;
    }
    this.turboBankedMs += turboChargeDurationMs;
    this.collected[removed.type] += 1;
    this.revision += 1;
    this.lastEvent = {
      bonusId: removed.id,
      occurredAtMs: simulationTimeMs,
      revision: this.revision,
      type: removed.type,
    };
    this.notify();
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }

  private reset(): void {
    this.collected = { turbo: 0 };
    this.lastEvent = null;
    this.population = null;
    this.previousPlayerPosition = null;
    this.revision = 0;
    this.turboActiveRemainingMs = 0;
    this.turboBankedMs = 0;
    this.playerRuntime.setSpeedMultiplier(1);
    this.notify();
  }

  private step(stepMilliseconds: number, simulationTimeMs: number): void {
    if (this.scene !== "gameplay") {
      return;
    }
    const player = this.playerRuntime.getSnapshot().player;
    let changed = false;
    if (this.turboActiveRemainingMs > 0) {
      this.turboActiveRemainingMs = Math.max(
        0,
        this.turboActiveRemainingMs - stepMilliseconds,
      );
      if (this.turboActiveRemainingMs === 0) {
        this.playerRuntime.setSpeedMultiplier(1);
      }
      changed = true;
    }
    if (player?.alive !== true || this.population === null) {
      this.previousPlayerPosition = player?.position ?? null;
      if (changed) {
        this.notify();
      }
      return;
    }
    const start = this.previousPlayerPosition ?? player.position;
    const candidates = this.population
      .getSnapshot()
      .filter(
        (bonus) =>
          segmentDistanceSquared(bonus.position, start, player.position) <=
          (bonus.radius + player.collision.radius) ** 2,
      );
    for (const bonus of candidates) {
      this.collect(bonus, simulationTimeMs);
    }
    this.previousPlayerPosition = { ...player.position };
    if (changed) {
      this.notify();
    }
  }
}
